import {ColumnProps} from "antd/lib/table";
import {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import OrderContext from "../../../context/order/context";
import {ISort, IStaticFilter} from "../../../models/base/base-query";
import {IOrder, IOrderOperationDuration} from "../../../models/order/response";
import {tableOnChange} from "../../../utils/helpers/table-sorts-filters";
import Space from "../../general/antd/space";
import ViewBtn from "../../general/table-components/actions/view-btn";
import Table from "../../general/table-components/table";
import Tag from "../../general/antd/tag";
import {durationBetweenDatesByDays} from "../../../utils/helpers/functions";

import {
    getOrderKindColor,
    getOrderKindName,
    getOrderStatus,
    getOrderStatusColor,
    getOrderStyle,
    getOrderTypeColor,
    getorderSchedulingTypeColor,
    getorderSchedulingTypeStyle,
    getDeliveryModeName,
    getDeliveryModeColor,
    OrderStatus,
} from "../../../models/order/enum";
import moment from "moment";
import {ImCheckmark, ImCross} from "react-icons/im";
import EditBtn from "../../general/table-components/actions/edit-btn";
import ChatBtn from "../../general/table-components/actions/chat-btn";
import TripBadge from "../trip-badge";
import DeliveryModeEditButton from "../delivery-mode-edit-button";
import CustomerSatisfactionStatusTag from "../customer-satisfaction-status-tag";
import OrderOperationDuration from "../order-operation-duration";
import styles from "./styles.module.scss";

interface IProps {
}

const LOW_COMPLETED_ORDERS_ALERT_MAX = 2;

const ORDER_ATTENTION_ALERT_EXCLUDED_STATUSES = new Set<number>([
    OrderStatus.COMPLETED_AND_PAID,
    OrderStatus.CANCELLED_BY_CUSTOMER,
    OrderStatus.CANCELLED_BY_ADMINISTRATION,
]);

const getClientCompletedOrdersCount = (order?: IOrder) =>
    order?.client?.completedOrdersCount ?? order?.client?.acceptedOrdersCount;

const hasLowCompletedOrdersCount = (order?: IOrder) => {
    const completedOrdersCount = getClientCompletedOrdersCount(order);

    return (
        typeof completedOrdersCount === "number" &&
        completedOrdersCount >= 0 &&
        completedOrdersCount <= LOW_COMPLETED_ORDERS_ALERT_MAX
    );
};

const shouldHighlightOrderAttention = (order?: IOrder) => {
    const status = order?.status;
    const matchesAttentionRule =
        Boolean(order?.isExpressDelivery) || hasLowCompletedOrdersCount(order);

    return (
        matchesAttentionRule &&
        typeof status === "number" &&
        !ORDER_ATTENTION_ALERT_EXCLUDED_STATUSES.has(status)
    );
};

const OrderTable: React.FC<IProps> = (props) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {list, actions, loading, query} = useContext(OrderContext);
    const renderDuration = (
        duration?: IOrderOperationDuration | null,
        seconds?: number | null,
    ) => (
        <OrderOperationDuration duration={duration} seconds={seconds} />
    );

    const operationTimingColumns: ColumnProps<IOrder>[] = [
        {
            title: t("total_cycle_minutes"),
            dataIndex: "operationTimings",
            align: "center",
            key: "totalCycleDuration",
            width: 140,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.totalCycleDuration,
                    record?.operationTimings?.totalCycleSeconds,
                ),
        },
        {
            title: t("store_acceptance_time_stats"),
            dataIndex: "operationTimings",
            align: "center",
            key: "storeAcceptanceDuration",
            width: 150,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.storeAcceptanceDuration,
                    record?.operationTimings?.storeAcceptanceSeconds,
                ),
        },
        {
            title: t("preparing_time_stats"),
            dataIndex: "operationTimings",
            align: "center",
            key: "preparingDuration",
            width: 130,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.preparingDuration,
                    record?.operationTimings?.preparingSeconds,
                ),
        },
        {
            title: t("modified_order_acceptance_time_stats"),
            dataIndex: "operationTimings",
            align: "center",
            key: "customerAcceptanceDuration",
            width: 170,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.customerAcceptanceDuration,
                    record?.operationTimings?.customerAcceptanceSeconds,
                ),
        },
        {
            title: t("accounting_finalization_time_stats"),
            dataIndex: "operationTimings",
            align: "center",
            key: "accountingFinalizationDuration",
            width: 170,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.accountingFinalizationDuration,
                    record?.operationTimings?.accountingFinalizationSeconds,
                ),
        },
        {
            title: t("driver_search_to_pickup"),
            dataIndex: "operationTimings",
            align: "center",
            key: "driverSearchToPickupDuration",
            width: 160,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.driverSearchToPickupDuration,
                    record?.operationTimings?.driverSearchToPickupSeconds,
                ),
        },
        {
            title: t("area_delivery_time_stats"),
            dataIndex: "operationTimings",
            align: "center",
            key: "deliveryDuration",
            width: 130,
            render: (_, record) =>
                renderDuration(
                    record?.operationTimings?.deliveryDuration,
                    record?.operationTimings?.deliverySeconds,
                ),
        },
    ];

    const columns: ColumnProps<IOrder>[] = [
        {
            title: t("id"),
            dataIndex: "id",
            key: "id",
            sorter: {
                multiple: 1,
            },
            width: 100,
            align: "center",
        },
        {
            title: t("number_id"),
            dataIndex: "number",
            key: "number",
            sorter: {
                multiple: 1,
            },
            width: 100,
            align: "center",
        },
        {
            title: t("delivery_store"),
            dataIndex: "deliveryStore",
            key: "deliveryStore",
            align: "center",
            width: 200,
            render: (_, record) => {
                return <Tag title={record?.deliveryStore?.name ?? "-"}/>;
            },
        },
        {
            title: t("trip_info"),
            dataIndex: "tripId",
            key: "tripId",
            align: "center",
            width: 150,
            render: (_, record) => {
                return (
                    <TripBadge
                        tripId={record?.tripId}
                        tripNumber={record?.tripNumber}
                        tripStatus={record?.tripStatus}
                    />
                );
            },
        },
        {
            title: t("delivery_mode"),
            dataIndex: "deliveryMode",
            key: "deliveryMode",
            align: "center",
            width: 200,
            render: (_, record) => {
                return (
                    <Space size="small" align="center">
                        <Tag
                            title={t(getDeliveryModeName(record?.deliveryMode))}
                            color={getDeliveryModeColor(record?.deliveryMode)}
                        />
                        <DeliveryModeEditButton
                            order={record}
                            onSuccess={() => actions.getData()}
                            size="small"
                        />
                    </Space>
                );
            },
        },
        {
            title: t("current_order_status"),
            dataIndex: "status",
            align: "center",
            key: "status",
            width: 250,
            render: (_, record) => {
                return (
                    <Tag
                        title={getOrderStatus(record?.status)}
                        color={getOrderStatusColor(record?.status)}
                    />
                );
            },
        },
        {
            title: t("customer_satisfaction_status"),
            dataIndex: "customerSatisfactionStatus",
            align: "center",
            key: "customerSatisfactionStatus",
            width: 160,
            render: (_, record) => (
                <CustomerSatisfactionStatusTag
                    status={record?.customerSatisfactionStatus}
                    fallbackStatus={record?.customerSatisfaction?.status}
                />
            ),
        },
        {
            title: t("is_express_delivery"),
            dataIndex: "isExpressDelivery",
            align: "center",
            key: "isExpressDelivery",
            width: 100,
            render: (_, record) => {
                return (
                    <Tag
                        title={t(getOrderKindName(record?.isExpressDelivery))}
                        color={getOrderKindColor(record?.isExpressDelivery)}
                    />
                );
            },
        },
        {
            title: t("order_scheduling_type"),
            dataIndex: "orderSchedulingType",
            align: "center",
            key: "orderSchedulingType",
            width: 120,
            render: (_, record) => {
                return (
                    <Tag
                        title={getorderSchedulingTypeStyle(record?.orderSchedulingType)}
                        color={getorderSchedulingTypeColor(record?.orderSchedulingType)}
                    />
                );
            },
        },
        {
            title: t("location_changed"),
            dataIndex: "locationChanged",
            align: "center",
            key: "locationChanged",
            width: 200,
            render: (_, record) => {
                if (!record?.locationChanged) {
                    return <ImCross color="red" />;
                }
                return (
                    <div style={{ fontSize: "12px", textAlign: "start" }}>
                        <div><strong>{t("browsing_store")}:</strong> {record?.browsingStore?.name ?? "-"}</div>
                        <div><strong>{t("browsing_total_price")}:</strong> {record?.browsingTotalPrice?.toLocaleString() ?? 0}</div>
                        <div><strong>{t("delivery_total_price")}:</strong> {record?.deliveryTotalPrice?.toLocaleString() ?? 0}</div>
                        <div>
                            <strong>{t("price_difference")}:</strong>{" "}
                            <span style={{ color: (record?.priceDifference ?? 0) > 0 ? "red" : "green", fontWeight: 600 }}>
                                {(record?.priceDifference ?? 0) > 0 ? "+" : ""}{record?.priceDifference?.toLocaleString() ?? 0}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            title: t("request_sourse_type"),
            dataIndex: "type",
            align: "center",
            key: "type",
            width: 200,
            render: (_, record) => {
                return (
                    <Tag
                        title={getOrderStyle(record?.type)}
                        color={getOrderTypeColor(record?.type)}
                    />
                );
            },
        },
        {
            title: t("client"),
            dataIndex: "client",
            align: "center",
            key: "client",
            width: 200,
            render: (_, record) => {
                return (
                    <Tag
                        title={record?.client?.firstName + " " + record?.client?.lastName}
                    />
                );
            },
        },
        {
            title: t("client_mobile"),
            dataIndex: "mobile",
            align: "center",
            key: "mobile",
            width: 200,
            render: (_, record) => {
                return record?.client?.cleanMobile ?? "-";
            },
        },
        {
            title: t("accepted_orders_count"),
            dataIndex: "acceptedOrdersCount",
            align: "center",
            key: "acceptedOrdersCount",
            width: 200,
            render: (_, record) => {
                return record?.client?.acceptedOrdersCount ?? "-";
            },
        },
        {
            title: t("last_completed_order_date"),
            dataIndex: "lastCompletedOrderDate",
            align: "center",
            key: "lastCompletedOrderDate",
            width: 200,
            render: (_, record) => {
                if (record?.client?.lastCompletedOrderDate === null) {
                    return "-";
                } else {
                    return moment(record?.client?.lastCompletedOrderDate).format("YYYY-MM-DD");
                }
            },
        },
        {
            title: t("days_form_last_order"),
            dataIndex: "DaysFormLastOrder",
            align: "center",
            key: "DaysFormLastOrder",
            width: 200,
            render: (_, record) => {
                if (record?.client?.lastCompletedOrderDate === null) {
                    return "0";
                } else {
                    return durationBetweenDatesByDays(moment(record?.client?.lastCompletedOrderDate), moment(record?.createdAt));
                }
            },
        },
        {
            title: t("driver"),
            dataIndex: "driver",
            align: "center",
            key: "driver",
            width: 200,
            render: (_, record) => {
                return (
                    <Tag
                        title={
                            (record?.driver?.firstName ?? "-") +
                            " " +
                            (record?.driver?.lastName ?? "-")
                        }
                    />
                );
            },
        },
        {
            title: t("driver_mobile"),
            dataIndex: "driver",
            align: "center",
            key: "driver",
            width: 200,
            render: (_, record) => {
                return record?.driver?.cleanMobile ?? "-";
            },
        },
        {
            title: t("store"),
            dataIndex: "store",
            align: "center",
            key: "store",
            width: 200,
            render: (_, record) => {
                return <Tag title={record?.store?.name ?? "-"}/>;
            },
        },
        {
            title: t("final_price"),
            dataIndex: "finalPrice",
            align: "center",
            key: "finalPrice",
            width: 200,
            render: (_, record) => {
                return record?.finalPrice ?? "-";
            },
        },
        {
            title: t("payment_status"),
            dataIndex: "paymentReceivedBy",
            align: "center",
            key: "paymentReceivedBy",
            width: 250,
            render: (_, record) => {
                if (record?.paymentReceivedBy) {
                    const accountantName = record.paymentReceivedBy.fullName ||
                        `${record.paymentReceivedBy.firstName} ${record.paymentReceivedBy.lastName}`;
                    const receivedDate = moment(record.paymentReceivedAt).format("YYYY-MM-DD HH:mm");
                    return (
                        <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "4px"}}>
                            <Tag title={t("payment_received")} color="green"/>
                            <span style={{fontSize: "12px", color: "#666"}}>{accountantName}</span>
                            <span style={{fontSize: "11px", color: "#999"}}>{receivedDate}</span>
                        </div>
                    );
                } else {
                    return <Tag title={t("payment_not_received")} color="gray"/>;
                }
            },
        },

        {
            title: t("emergency"),
            dataIndex: "isEmergency",
            align: "center",
            key: "isEmergency",
            width: 200,
            render: (_, record) => {
                return (
                    <>
                        {record.isEmergency ? (
                            <ImCheckmark color="green"/>
                        ) : (
                            <ImCross color="red"/>
                        )}
                    </>
                );
            },
        },
        {
            title: t("is_recipe"),
            dataIndex: "isRecipe",
            align: "center",
            key: "isRecipe",
            width: 200,
            render: (_, record) => {
                return (
                    <>
                        {record.isRecipe ? (
                            <ImCheckmark color="green"/>
                        ) : (
                            <ImCross color="red"/>
                        )}
                    </>
                );
            },
        },

        {
            title: t("created_at"),
            dataIndex: "createdAt",
            align: "center",
            key: "createdAt",
            width: 200,
            render: (_, record) => {
                return moment(record?.createdAt).format("YYYY-MM-DD");
            },
        },

        ...operationTimingColumns,

        {
            title: t("actions"),
            width: 180,
            dataIndex: "",
            align: "center",
            fixed: "right",
            key: "x",
            render: (_: any, record) => (
                <Space>
                    <ViewBtn
                        onClick={(e) => {
                            const url = `/orders/details/${record?.id}`;
                            if (e.ctrlKey || e.metaKey) {
                                window.open(url, "_blank");
                            } else {
                                navigate(url);
                            }
                        }}
                    />
                    <EditBtn
                        onClick={(e) => {
                            const url = `/orders/update/${record?.id}`;
                            if (e.ctrlKey || e.metaKey) {
                                window.open(url, "_blank");
                            } else {
                                navigate(url);
                            }
                        }}
                    />
                    <ChatBtn
                        userId={record?.client?.id}
                    />
                </Space>
            ),
        },
    ];

    // Filters && Sorts
    let tableFiltersProps = {};
    const [sorts, setSorts] = useState<ISort[]>([]);
    const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

    useEffect(() => {
        sorts.length > 0 &&
        actions.setQuery({
            ...query,
            orders: sorts,
        });
    }, [sorts]);

    return (
        <>
            <Table<any>
                rowKey="id"
                showPagination={true}
                columns={columns}
                dataSource={list?.data ?? []}
                rowClassName={(record) =>
                    shouldHighlightOrderAttention(record)
                        ? styles.orderAttentionAlertRow
                        : ""
                }
                loading={loading.includes("list") || list == undefined}
                size="small"
                total={list?.total}
                pageSize={query.perPage}
                page={query.page}
                onPaginationChange={(page, pageSize) => {
                    actions.setQuery({
                        ...query,
                        page,
                        perPage: pageSize,
                    });
                }}
                scroll={{
                    x: 3000,
                }}
                onChange={(_, tableFilters, tabelSorters) => {
                    tableOnChange(
                        tableFilters,
                        tabelSorters,
                        tableFiltersProps,
                        setSorts,
                        setStaticFilters
                    );
                }}
            />
        </>
    );
};

export default OrderTable;
